import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, authImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Phone, Mail, MessageCircle, MapPin, Building2, Layers, CalendarClock, BadgeCheck, Crown, Award } from "lucide-react";
import { Laurel } from "@/components/LaurelBadge";

const Row = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-navy/5 last:border-0">
      <Icon className="w-5 h-5 text-gold mt-0.5 shrink-0" />
      <div>
        <p className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">{label}</p>
        <p className="text-navy-deep font-medium">{value}</p>
      </div>
    </div>
  );
};

const ClubRoles = ({ memberships }) => {
  if (!memberships || memberships.length === 0) return null;
  const multi = memberships.length > 1;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-navy/5 last:border-0" data-testid="club-roles">
      <Building2 className="w-5 h-5 text-gold mt-0.5 shrink-0" />
      <div className="min-w-0 w-full">
        <p className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
          {multi ? "Club e cariche" : "Club e carica"}{multi && <span className="ml-1.5 text-gold">({memberships.length})</span>}
        </p>
        <ul className="mt-1.5 space-y-2">
          {memberships.map((ms, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0 mt-2" />
              <div className="min-w-0">
                <p className="text-navy-deep font-semibold leading-tight">{ms.club}</p>
                <p className="text-xs uppercase tracking-wider font-bold text-gold">{ms.ruolo || "Socio"}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const MultiRow = ({ icon: Icon, label, values }) => {
  if (!values || values.length === 0) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-navy/5 last:border-0" data-testid={`multirow-${label.toLowerCase()}`}>
      <Icon className="w-5 h-5 text-gold mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
          {label}{values.length > 1 && <span className="ml-1.5 text-gold">({values.length})</span>}
        </p>
        {values.length > 1 ? (
          <ul className="mt-1 space-y-1">
            {values.map((v, i) => (
              <li key={i} className="flex items-center gap-2 text-navy-deep font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />{v}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-navy-deep font-medium">{values[0]}</p>
        )}
      </div>
    </div>
  );
};

export default function MemberDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/members/${id}`).then((r) => setM(r.data)).catch(() => setErr("Socio non trovato"));
  }, [id]);

  if (err) return <div className="text-center py-16 text-muted-foreground">{err}</div>;
  if (!m) return <div className="text-center py-16 text-navy">Caricamento…</div>;

  const phone = (m.telefono || "").replace(/\s/g, "");
  const wa = phone.replace(/[^0-9]/g, "");
  const clubs = (m.clubs && m.clubs.length ? m.clubs : [m.club]).filter(Boolean);
  const divs = (m.divisioni && m.divisioni.length ? m.divisioni : [m.divisione]).filter(Boolean);
  const memberships = (m.memberships && m.memberships.length)
    ? m.memberships
    : clubs.map((c) => ({ club: c, ruolo: m.carica || "" }));

  return (
    <div className="max-w-4xl mx-auto" data-testid="member-detail">
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-navy-hover transition-colors mb-6" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Torna all'elenco
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <aside className="md:col-span-1">
          <div className="bg-white rounded-lg border border-navy/10 p-6 text-center">
            {m.photo_url ? (
              <img src={authImageUrl(m.photo_url)} alt={m.cognome}
                className="w-32 h-32 rounded-lg object-cover border-2 border-gold/50 mx-auto"
                onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <div className="w-32 h-32 rounded-lg bg-navy mx-auto flex items-center justify-center border-2 border-gold/50">
                <span className="font-display text-gold font-bold text-4xl">
                  {(m.nome?.[0] || "").toUpperCase()}{(m.cognome?.[0] || "").toUpperCase()}
                </span>
              </div>
            )}
            <h1 className="font-display text-2xl font-bold text-navy mt-4">{m.nome} {m.cognome}</h1>
            {m.onorario && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/10 border border-gold/40 text-[#8a6d2f]" data-testid="honorary-badge">
                <Laurel className="w-4 h-4 shrink-0" />
                <span className="text-xs font-bold uppercase tracking-wider">Socio Onorario</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-6">
              <a href={phone ? `tel:${phone}` : undefined} data-testid="action-call"
                className={`flex flex-col items-center gap-1 py-3 rounded-md border transition-colors duration-200 ${phone ? "border-navy/15 hover:bg-navy hover:text-white text-navy" : "border-navy/5 text-muted-foreground pointer-events-none opacity-50"}`}>
                <Phone className="w-5 h-5" /><span className="text-xs font-semibold">Chiama</span>
              </a>
              <a href={m.email ? `mailto:${m.email}` : undefined} data-testid="action-email"
                className={`flex flex-col items-center gap-1 py-3 rounded-md border transition-colors duration-200 ${m.email ? "border-navy/15 hover:bg-navy hover:text-white text-navy" : "border-navy/5 text-muted-foreground pointer-events-none opacity-50"}`}>
                <Mail className="w-5 h-5" /><span className="text-xs font-semibold">Email</span>
              </a>
              <a href={wa ? `https://wa.me/${wa}` : undefined} target="_blank" rel="noreferrer" data-testid="action-whatsapp"
                className={`flex flex-col items-center gap-1 py-3 rounded-md border transition-colors duration-200 ${wa ? "border-[#25D366]/40 text-[#128C4B] hover:bg-[#25D366] hover:text-white" : "border-navy/5 text-muted-foreground pointer-events-none opacity-50"}`}>
                <MessageCircle className="w-5 h-5" /><span className="text-xs font-semibold">WhatsApp</span>
              </a>
            </div>
          </div>
        </aside>

        <div className="md:col-span-2">
          <div className="bg-white rounded-lg border border-navy/10 p-6">
            <h2 className="font-display text-xl font-semibold text-navy mb-2">Scheda socio</h2>
            {(m.district_roles?.length || m.lieutenant_of?.length) ? (
              <div className="mb-4 rounded-md bg-navy text-white p-4" data-testid="district-roles">
                <p className="text-[11px] uppercase tracking-[0.12em] text-gold font-bold flex items-center gap-1.5">
                  <Crown className="w-4 h-4" />Incarichi distrettuali
                </p>
                <ul className="mt-2 space-y-1.5">
                  {(m.district_roles || []).map((r, i) => (
                    <li key={`o${i}`} className="flex items-center gap-2 font-semibold">
                      <Award className="w-4 h-4 text-gold shrink-0" />{r}
                    </li>
                  ))}
                  {(m.lieutenant_of || []).map((d, i) => (
                    <li key={`l${i}`} className="flex items-start gap-2 font-semibold">
                      <Award className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                      <span>Luogotenente Governatore<span className="block text-xs font-normal text-white/70">{d}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <ClubRoles memberships={memberships} />
            <MultiRow icon={Layers} label="Divisione" values={divs} />
            <Row icon={Mail} label="Email" value={m.email} />
            <Row icon={Phone} label="Telefono" value={m.telefono} />
            <Row icon={MapPin} label="Indirizzo" value={m.indirizzo} />
            <Row icon={CalendarClock} label="Socio Kiwanis dal" value={m.join_date} />
            {m.member_number && <Row icon={BadgeCheck} label="Numero di membro" value={m.member_number} />}
          </div>
        </div>
      </div>
    </div>
  );
}
